import { useEffect, useState } from 'react'
import { formatPhone } from '../../utils/phone'
import { updateTenant, deleteTenant, restoreTenant, getOwnerContact, trialDaysLeft, type Tenant } from '../../services/superadmin'
import { type Features, type TenantType } from '../../types'
import { PanelGate } from './PanelGate'
import { StaffPanel } from './StaffPanel'
import { MenuPanel } from './MenuPanel'
import { FeatureTable } from './FeatureTable'
import { featuresFor, formatPrice, PLANS, type PlanId, type PlanPeriod } from '../../plans'
import { CheckIcon } from '../icons'

/**
 * Edits an institution already open, and can close it down.
 *
 * Editing goes straight through RLS (`updateTenant`), no second secret, no
 * Edge Function. Renaming a kurum or fixing its discount percentage is not
 * the kind of action that needs a bank-style second factor.
 *
 * Deleting is the opposite: it takes every staff login, every order, the
 * whole menu with it, and cannot be undone. It needs typing the tenant's own
 * id to confirm (so a stray click on the wrong row cannot do it) and then
 * PANEL_SECRET (see functions/admin/index.ts), same as appointing an owner.
 */
export function TenantForm({ tenant, onSaved }: { tenant: Tenant; onSaved: () => void }) {
  const [type, setType] = useState<TenantType>(tenant.type)
  const [features, setFeatures] = useState<Features>(tenant.features)
  const [plan, setPlan] = useState<PlanId | null>(tenant.plan)
  const [period, setPeriod] = useState<PlanPeriod | null>(tenant.plan_period)
  const [saving, setSaving] = useState(false)
  // Patronun adı ve giriş adresi: telefonun yanında, aranacak ya da yazılacak
  // kişi. Kurum değişince yeniden okunuyor.
  const [owner, setOwner] = useState<{ name: string; email: string } | null>(null)
  useEffect(() => {
    let live = true
    setOwner(null)
    getOwnerContact(tenant.id)
      .then((o) => live && setOwner(o))
      .catch(() => live && setOwner(null))
    return () => {
      live = false
    }
  }, [tenant.id])
  const [error, setError] = useState<string | null>(null)

  const [confirmId, setConfirmId] = useState('')
  const [showGate, setShowGate] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [restoreGate, setRestoreGate] = useState(false)
  const [restoring, setRestoring] = useState(false)

  // Switching to a different tenant in the list must reset the form to that
  // tenant's own values, not keep whatever the previous one's fields held.
  useEffect(() => {
    setType(tenant.type)
    setFeatures(tenant.features)
    setPlan(tenant.plan)
    setPeriod(tenant.plan_period)
    setError(null)
    setConfirmId('')
    setShowGate(false)
    setDeleteError(null)
    setRestoreGate(false)
  }, [tenant])

  async function onRestoreConfirmed(secret: string) {
    setRestoring(true)
    setDeleteError(null)
    try {
      await restoreTenant(secret, tenant.id)
      onSaved()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Açılamadı.')
    } finally {
      setRestoring(false)
      setRestoreGate(false)
    }
  }

  // Patronun silme talebi (hesap-silme.sql): 30 gün sonra kalıcı silinir.
  const delAt = tenant.deletion_requested_at ? Date.parse(tenant.deletion_requested_at) : null
  const delDue = delAt !== null ? delAt + 30 * 86_400_000 : null
  const delLeft = delDue !== null ? Math.ceil((delDue - Date.now()) / 86_400_000) : null
  const trDate = (ms: number) => new Date(ms).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })

  async function onDeleteConfirmed(secret: string) {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteTenant(secret, tenant.id)
      onSaved()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Silinemedi.')
      setShowGate(false)
    } finally {
      setDeleting(false)
    }
  }

  /**
   * Deneme süresi: anında kaydediliyor, formun Kaydet'ini beklemiyor, biri
   * telefonda "süremi uzatır mısınız" derken tek dokunuş.
   *
   * Uzatma bugünden değil bitiş gününden sayılıyor (bitmişse bugünden):
   * süresi dolmadan uzatılan kurum kalan günlerini kaybetmemeli.
   */
  const [trialBusy, setTrialBusy] = useState(false)
  const setTrial = async (endsAt: string | null) => {
    setTrialBusy(true)
    setError(null)
    try {
      await updateTenant(tenant.id, { trial_ends_at: endsAt })
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deneme süresi değiştirilemedi.')
    } finally {
      setTrialBusy(false)
    }
  }
  const extendTrial = () => {
    const from = Math.max(Date.now(), tenant.trial_ends_at ? Date.parse(tenant.trial_ends_at) : 0)
    void setTrial(new Date(from + 14 * 86_400_000).toISOString())
  }
  const left = trialDaysLeft(tenant)

  const onSubmit = async () => {
    setSaving(true)
    setError(null)
    try {
      await updateTenant(tenant.id, {
        type,
        features,
        plan,
        plan_period: plan ? period : null,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tenant-form">
      <div className="tenant-form__col">
      {/* İletişim (31 Eki 2026): etiketli, hafif zeminli kutuda. Ad ve kod
          çekmecenin başlığında. */}
      <dl className="tenant-form__info">
        <div>
          <dt>Yetkili</dt>
          <dd>{owner ? owner.name : <span className="is-empty">atanmamış</span>}</dd>
        </div>
        <div>
          <dt>E-posta</dt>
          <dd>
            {owner?.email ? (
              <span style={{ userSelect: 'all' }}>{owner.email}</span>
            ) : (
              <span className="is-empty">yok</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Telefon</dt>
          <dd>
            {tenant.contact_phone ? (
              <span style={{ userSelect: 'all' }}>{formatPhone(tenant.contact_phone)}</span>
            ) : (
              <span className="is-empty">girilmemiş</span>
            )}
          </dd>
        </div>
      </dl>

      {delAt !== null && delDue !== null && delLeft !== null && (
        <div className="tenant-form__trial is-over">
          <span>
            {delLeft > 0
              ? `Patron hesabı silmek istedi, kalıcı silmeye ${delLeft} gün`
              : 'Silme süresi doldu: aşağıdan "İşletmeyi sil" ile kalıcı silin.'}{' '}
            <em>
              (talep {trDate(delAt)}, silme {trDate(delDue)})
            </em>
          </span>
          <div className="tenant-form__trial-actions">
            <button type="button" className="btn btn--outline" disabled={restoring} onClick={() => setRestoreGate(true)}>
              {restoring ? 'Açılıyor…' : 'Vazgeçti, geri aç'}
            </button>
          </div>
        </div>
      )}

      {left !== null && (
        <div className={`tenant-form__trial ${left <= 0 ? 'is-over' : ''}`}>
          <span>
            {left <= 0
              ? 'Deneme süresi doldu, yeni sipariş alamıyor.'
              : `Deneme: ${left} gün kaldı`}{' '}
            <em>
              (bitiş{' '}
              {new Date(tenant.trial_ends_at!).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
              })}
              )
            </em>
          </span>
          <div className="tenant-form__trial-actions">
            <button type="button" className="btn btn--outline" disabled={trialBusy} onClick={extendTrial}>
              +14 gün uzat
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={trialBusy}
              onClick={() => void setTrial(null)}
            >
              Kalıcı yap
            </button>
          </div>
        </div>
      )}




      {/* Paket (29 Eki 2026; 31 Eki kartlara çevrildi, kullanıcı: "mantığını
          anlamadım"). Seçince paketin özellikleri aşağıdaki tabloda açılır,
          öteki paket özellikleri kapanır, ek modüller kalır. Kaydet'le yazılır. */}
      <section className="plan-pick">
        <div className="plan-pick__head">
          <span className="codes__features-label">Paket</span>
          <p>
            Paket seçince o paketin özellikleri aşağıdaki tabloda açılır, öteki paketlerinkiler
            kapanır. Ek modüller olduğu gibi kalır. Kaydet'e basınca geçerli olur.
          </p>
        </div>

        <div className="plan-cards" role="radiogroup" aria-label="Paket">
          <button
            type="button"
            role="radio"
            aria-checked={plan === null}
            className={`plan-card ${plan === null ? 'is-on' : ''}`}
            onClick={() => setPlan(null)}
          >
            <span className="plan-card__tick">{plan === null && <CheckIcon />}</span>
            <strong>Paketsiz</strong>
            <span className="plan-card__price">Ücret yok</span>
            <small>Özellikler tek tek seçilir</small>
          </button>
          {PLANS.map((p) => {
            const yearly = plan !== null && period === 'yillik'
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={plan === p.id}
                className={`plan-card ${plan === p.id ? 'is-on' : ''}`}
                onClick={() => {
                  setPlan(p.id)
                  if (!period) setPeriod('aylik')
                  setFeatures(featuresFor(p.id, features))
                }}
              >
                <span className="plan-card__tick">{plan === p.id && <CheckIcon />}</span>
                <strong>{p.name}</strong>
                <span className="plan-card__price">
                  {yearly ? `${formatPrice(p.yearly)} / yıl` : `${formatPrice(p.monthly)} / ay`}
                </span>
                <small>{p.staff === null ? 'Sınırsız personel' : `${p.staff} personele kadar`}</small>
              </button>
            )
          })}
        </div>

        {plan && (
          <div className="plan-period">
            <span className="codes__features-label">Ödeme dönemi</span>
            <div className="plan-seg plan-seg--small" role="radiogroup" aria-label="Ödeme dönemi">
              {(
                [
                  ['aylik', 'Aylık'],
                  ['yillik', 'Yıllık (2 ay bedava)'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={period === id}
                  className={period === id ? 'is-on' : ''}
                  onClick={() => setPeriod(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Only reachable here. An owner opening their own Ayarlar cannot switch
          these on, a database trigger refuses the write, because a feature
          they have not been given is not theirs to grant themselves. */}
      <FeatureTable label="Özellikler" features={features} onChange={setFeatures} />

      {error && <p className="superadmin__error">{error}</p>}

      <button
        type="button"
        className="btn btn--primary"
        disabled={saving}
        onClick={onSubmit}
      >
        {saving ? 'Kaydediliyor…' : 'Kaydet'}
      </button>

      </div>

      <div className="tenant-form__col">
      <StaffPanel tenantId={tenant.id} />
      <MenuPanel tenantId={tenant.id} />

      <div className="tenant-form__danger">
        <h4>İşletmeyi kapat</h4>
        <p className="superadmin__hint">
          Bu işletmenin tüm personelini, menüsünü ve siparişlerini geri alınamaz
          şekilde siler. Onaylamak için işletme kodunu ("{tenant.id}") yazın.
        </p>
        <input
          value={confirmId}
          onChange={(e) => setConfirmId(e.target.value)}
          placeholder={tenant.id}
          autoCapitalize="none"
          spellCheck={false}
        />
        {deleteError && <p className="superadmin__error">{deleteError}</p>}
        <button
          type="button"
          className="btn btn--danger"
          disabled={deleting || confirmId.trim() !== tenant.id}
          onClick={() => setShowGate(true)}
        >
          {deleting ? 'Siliniyor…' : 'İşletmeyi sil'}
        </button>
      </div>
      </div>

      {restoreGate && (
        <PanelGate
          title={`"${tenant.display_name}" işletmesini yeniden aç`}
          onConfirm={onRestoreConfirmed}
          onCancel={() => setRestoreGate(false)}
        />
      )}

      {showGate && (
        <PanelGate
          title={`"${tenant.display_name}" işletmesini sil`}
          onConfirm={onDeleteConfirmed}
          onCancel={() => setShowGate(false)}
        />
      )}
    </div>
  )
}
